import React, { useMemo } from "react";
import TextBox from "../../common/TextBox";
import {
  Grid,
  Typography,
  ListItemText,
  GridSize,
  Box,
  List,
  ListItem,
  Button,
  Checkbox,
  Radio,
  Tooltip,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AppConstants from "../../constants";
import {
  driverStatusField,
} from "../../mocks/express/response.transforms";
import refreshIcon from "../../assets/icons/refresh.svg";
import clockIcon from "../../assets/icons/clock.svg";
import Lens from "@material-ui/icons/Lens";
import ChipComponent from "../../common/Chips";
import CloseIcon from "@material-ui/icons/Close";
import { TextConstants } from "constants/TextConstants";
import { VehicleFilterPopover } from "./VehicleFilterPopover";
import { getVehicleIcon, formatSnakeCase } from "utils/helpers.utils";
import { COLORS } from "config/theme/baseTheme";
import { getTimeDifference } from "utils/time.utils";

interface HeaderColumn {
  label: string | React.ReactNode; // Column label or custom JSX
  xs: GridSize; // Grid column size
  style?: React.CSSProperties; // Optional custom styles
}

interface GenericHeaderProps {
  columns: HeaderColumn[]; // Array of column configurations
  className?: string; // Optional class name for the container
}

interface DriverListItemProps {
  driver: any;
  isSelected: boolean;
  handleListItemClick: (event: any, driver: any) => void;
  classes: any;
  selectionType?: "checkbox" | "radio"; // Determines the selection type
  isSuggested?: boolean;
  isPrimary?: boolean;
}

// Utility function to get driver category class
const getDriverCategoryClass = (category: string) => {
    const upperCategory = category.toUpperCase();
    if (upperCategory === AppConstants.EXPRESS_CONSTANTS.DRIVERS_CATEGORY.ASSIGNED) {
      return "assignedList";
    }
    if (upperCategory === AppConstants.EXPRESS_CONSTANTS.DRIVERS_CATEGORY.UNASSIGNED) {
      return "unassignedList";
    }
    return "";
  };

  const isDriverAvailableOrAssignedAtHub = (driver: any): boolean => {
    return (
      driver.status ===
        AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DRIVER_STATUSES.AVAILABLE.value ||
      (driver.status ===
        AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DRIVER_STATUSES.ASSIGNED.value &&
        driver.proximityToHub === 0.0)
    );
  };  



const DriverListItem: React.FC<DriverListItemProps> = ({
    driver,
    isSelected,
    handleListItemClick,
    classes,
    selectionType = "radio", // Default to "radio"
    isSuggested = false,
    isPrimary = false,
  }) => {
    const vehicleIconInfo = useMemo(() => getVehicleIcon(driver?.vehicleType), [driver?.vehicleType]);
    const isAtLimit = isPrimary && driver?.restrictAssignment;
    const dimStyle = isAtLimit ? { opacity: 0.6 } : undefined;

    return (
      <ListItem
        className={`list-container ${getDriverCategoryClass(driver.category)}`}
        button
        selected={isSelected && !isAtLimit}
        onClick={(event: any) => !isAtLimit && handleListItemClick(event, driver)}
        classes={{ selected: classes.selected }}
        style={isAtLimit ? { cursor: "not-allowed" } : undefined}
      >
        {/* Selection Control (Checkbox or Radio) */}
        {selectionType === "checkbox" ? (
          <Checkbox
            size="small"
            color="primary"
            checked={isSelected && !isAtLimit}
            disabled={isAtLimit}
            style={{ margin: "4px", ...dimStyle }}
          />
        ) : (
          <Radio
            size="small"
            color="primary"
            checked={isSelected && !isAtLimit}
            disabled={isAtLimit}
            style={{ padding: "4px", ...dimStyle }}
          />
        )}
  
        <ListItemText>
          <Grid container justify="space-between" alignItems="center">
            {/* Driver Name */}
            <Grid item xs={4} className={classes.driverNameCell} style={dimStyle}>
              <span>{driver.driverName}</span>
            </Grid>

            {/* Vehicle Type */}
            <Grid item xs={2} className={classes.alignCenter} style={dimStyle}>
              <img src={vehicleIconInfo.icon} alt={vehicleIconInfo.alt} />
            </Grid>

            {/* Driver Status */}
            <Grid
              item
              xs={3}
              className={["driverStatus", classes.driverStatusCell, driverStatusField("color", driver.status)].join(" ")}
              style={dimStyle}
            >
              {formatSnakeCase(driver?.status)}
            </Grid>

            {/* Proximity to Hub */}
            <Grid item xs={2} className={classes.proximityCell} style={dimStyle}>
              <Grid container direction="column">
                <Grid item>
                  {driver.category === AppConstants.EXPRESS_CONSTANTS.DRIVERS_CATEGORY.OTHERS
                    ? "-"
                    : driver.proximityToHub === 0.0
                    ? "In hub"
                    : `${driver.proximityToHub} km`}
                </Grid>
                {isDriverAvailableOrAssignedAtHub(driver) && driver?.hubCheckInTime ? (
                  <Grid item className={classes.waitTimeRow}>
                    <img src={clockIcon} alt="Clock" className={classes.clockIcon} />
                    <span className={classes.waitTimeText}>
                      {driver?.hubCheckInTime && getTimeDifference(driver?.hubCheckInTime)}
                    </span>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>

            {/* Assignments */}
            <Grid item xs={1} className={classes.assignmentsCell} style={isAtLimit? { color:  COLORS.ALIZARIN_CRIMSON} : undefined}>
              {isPrimary && driver?.maxAssignmentsLimit ? `${driver?.assignments}/${driver?.maxAssignmentsLimit}` : driver?.assignments}
              {isAtLimit && (
                <Tooltip title={TextConstants.LIMIT_REACHED} arrow>
                  <InfoOutlinedIcon className={classes.limitIcon} />
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </ListItemText>
      </ListItem>
    );
  };


export const DriversList = React.memo(
    ({
        listToShow,
        selectedDrivers,
        selectedIndex,
        handleListItemClick,
        classes,
        selectionType,
        isPrimary,
      }: {
        listToShow: any[];
        selectedDrivers?: any[];
        selectedIndex?: number | null; // Optional selected index for single selection
        handleListItemClick: (event: any, driver: any) => void;
        classes: any;
        selectionType?: "checkbox" | "radio";
        isPrimary?: boolean;
      }) =>{
    return listToShow && listToShow.length ? (
      <React.Fragment>
        {listToShow.map((driver: any, index: number) => (
          <DriverListItem
            key={index}
            driver={driver}
            isSelected={
              selectedIndex !== undefined && selectedIndex !== null
                ? selectedIndex === index // Single selection logic
                : selectedDrivers?.includes(driver) || false // Multi-selection logic with default value
            }
            handleListItemClick={handleListItemClick}
            classes={classes}
            selectionType={selectionType}
            isPrimary={isPrimary}
          />
        ))}
      </React.Fragment>
    ) : (
      <Typography className={classes.noData}>{TextConstants.NO_DRIVER}</Typography>
    );
  }
);

export const DriversSearchHeader = React.memo(
  ({
    consignment,
    handleSearchBoxTextChange,
    searchValue,
    searchBoxRef,
    classes,
    fetchDriversLists,
    countryCode,
    title,
    anchorEl,
    openVehiclePopup,
    pendingVehicles,
    selectedCount,
    isAllSelected,
    isIndeterminate,
    vehicleOptions,
    handleVehiclePopupOpen,
    handleVehiclePopupClose,
    handleVehicleToggle,
    handleSelectAll,
    handleApplyVehicleFilter,
    handleClearVehicleFilter,
  }: any) => {
    return (
      <>
        <Grid className="driverComponent">
          <Typography className="driverTitle">{title}</Typography>
          <img
            src={refreshIcon}
            alt="refresh"
            className="refresh"
            role="button"
            onClick={() => fetchDriversLists(consignment, countryCode)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                fetchDriversLists(consignment, countryCode);
              }
            }}
          />
        </Grid>

        <Grid container className={classes.searchHeaderContainer} spacing={1}>
          <Grid item xs={4} className={classes.legendContainer}>
            <Grid container>
              <Grid item className={classes.main} xs={6}>
                <Lens className={classes.unassignedStatusIcon} />
                <Typography className={classes.legend}>
                  {TextConstants.UNASSIGNED}
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                className={`${classes.main} ${classes.assignedLegend}`}
              >
                <Lens className={classes.assignedStatusIcon} />
                <Typography className={classes.legend}>
                  {TextConstants.ASSIGNED}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {(vehicleOptions && vehicleOptions.length) > 0 && (
            <Grid item xs={4} className={classes.vehicleSelectContainer}>
              <VehicleFilterPopover
                anchorEl={anchorEl}
                open={openVehiclePopup}
                pendingVehicles={pendingVehicles}
                selectedCount={selectedCount}
                isAllSelected={isAllSelected}
                isIndeterminate={isIndeterminate}
                vehicleOptions={vehicleOptions}
                classes={classes}
                onOpen={handleVehiclePopupOpen}
                onClose={handleVehiclePopupClose}
                onToggle={handleVehicleToggle}
                onSelectAll={handleSelectAll}
                onApply={handleApplyVehicleFilter}
                onClear={handleClearVehicleFilter}
              />
            </Grid>
          )}
          <Grid item xs={(vehicleOptions && vehicleOptions.length) > 0 ? 4 : 8} className={classes.searchTextbox}>
            <TextBox
              variant="outlined"
              onChange={handleSearchBoxTextChange}
              value={searchValue}
              textBoxId="searchTextBox"
              type="text"
              placeholderText={TextConstants.SEARCH_BY_DRIVER_NAME}
              inputRef={searchBoxRef}
              style={{ width: "100%" }}
            />
          </Grid>
        </Grid>
      </>
    );
  }
);

export const DriversFooter = React.memo(
  ({ isDriverSelected, handleAssignDriver, handleCancel, handleAssignClubbed, hasClubbingSuggestions, clubbingSuggestionLength, isAssignDisabled }: any) => {
    return (
      <Grid className="buttonsContainer" container>
        {hasClubbingSuggestions && (
          <Grid item xs={6}>
            <Button
              className="userBtn primary"
              variant="contained"
              onClick={handleAssignClubbed}
              disabled={!isDriverSelected }
            >
              {clubbingSuggestionLength === 1 ? TextConstants.ASSIGN_MONO : TextConstants.ASSIGN_CLUBBED}
            </Button>
          </Grid>
        )}
        <Grid
          item
          xs={hasClubbingSuggestions ? 6 : 12}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            className="userBtn secondary"
            variant="contained"
            onClick={handleCancel}
          >
            {AppConstants.BUTTONS.CLEAR_NEW}
          </Button>
          <Button
            className="userBtn primary"
            variant="contained"
            disabled={isAssignDisabled}
            onClick={handleAssignDriver}
          >
            {AppConstants.BUTTONS.ASSIGN}
          </Button>
        </Grid>
      </Grid>
    );
  }
);

export const DriversHeader: React.FC<GenericHeaderProps> = ({
  columns,
  className,
}) => {
  return (
    <Grid
      container
      className={className}
      justify="space-between"
      alignItems="center"
    >
      {columns.map((column, index) => (
        <Grid item xs={column.xs} key={index} style={column.style}>
          {column.label}
        </Grid>
      ))}
    </Grid>
  );
};

export const AssignedDriverChips = ({
  title,
  drivers,
  removeDriver,
  classes,
  noDataMessage,
  editable = true,
}: {
  title: string;
  drivers: { id: string; name: string; alt: string }[];
  removeDriver: (id: string) => void;
  classes: any;
  noDataMessage: string;
  editable?: boolean;
}) => {
  return (
    <>
      <Grid className={classes.assignedDriversContainer} container>
        <Grid item style={{ padding: "0px 8px" }}>
          <Typography className="driverTitle">{title}</Typography>
        </Grid>
        <Grid item style={{ padding: "0px 8px" }}>
          <Grid container className="chipContainer">
            {drivers && drivers.length > 0 ? (
              drivers.map((driver, index) => (
                <Grid
                  item
                  direction="row"
                  className={classes.pilles}
                  key={index}
                >
                  <ChipComponent
                    item={driver} // Pass the transformed driver object
                    editable={editable}
                    onDeleteCallback={() => removeDriver(driver.id)} // Use the transformed id
                    DeleteIcon={CloseIcon}
                  />
                </Grid>
              ))
            ) : (
              <Typography className={classes.noData}>
                {noDataMessage}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
